/**
 * MessagesPage — central messaging area at `/dashboard/nachrichten`
 * (architecture-messaging-platform.md §9.2 + design-messaging-ux.md §2).
 *
 * Visible for BOTH roles (owner and advisor) — the page itself is reachable
 * regardless of access (architecture §6: "Route existiert zwar, aber ohne
 * Einstiegspunkt" when there is no Nav entry), but its content is gated
 * internally so no Firestore listener mounts unless
 * `canUseMessaging(effectiveScopeUid)` is true (beta-tenant allowlist OR the
 * general flag).
 *
 * DEVIATION FROM THE M-MSG-2 API CONTRACT (documented per coder role rules,
 * see changelog.md): the M-MSG-2 API (`messagesApi.ts`/`useMessages`) only
 * exposes a SINGLE-thread subscription (`subscribeThread`) — there is no
 * "list all threads with activity for this tenant" query. Building the
 * Thread-Liste (design §2.2) therefore requires an additional, page-local
 * Firestore listener over `messages where userId==scopeUid orderBy createdAt
 * desc`, which this file owns directly (same direct-Firestore-query pattern
 * `InvoiceArchive.tsx` already uses for its own list). It reuses the exact
 * composite index already defined for the M-MSG-1 Rules
 * (`userId ASC, createdAt ASC` — §3.5), so no new index is required. Per-
 * thread unread badges are filled in via the exported `getThreadUnreadCount`
 * (a public part of the M-MSG-2 contract), NOT invented.
 *
 * @module MessagesPage
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  limit as fsLimit,
  onSnapshot,
  doc,
  getDoc,
  DocumentData,
  QuerySnapshot,
} from 'firebase/firestore';
import { IconMessages, IconInvoice, IconExpense } from '../icons';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { canUseMessaging } from '../config/messagingAccess';
import { getThreadUnreadCount, markGlobalRead } from '../api/messagesApi';
import { Message, MessageContext, MessageContextType, ReceiptRefCollection } from '../types/messagingTypes';
import MessageThreadView, { MessageThreadContextSummary } from '../components/messaging/MessageThreadView';
import { buildThreadSummary, ThreadDocData, ThreadSummaryLabels } from '../components/messaging/threadSummary';
import logger from '../utils/logger';

/** Number of most-recent tenant messages considered when deriving the thread list (v1 bound). */
const THREAD_SCAN_LIMIT = 300;

type FilterKey = 'all' | 'general' | 'invoices' | 'receipts';

interface ThreadSummary {
  threadKey: string;
  context: MessageContext;
  refCollection: ReceiptRefCollection | null;
  lastMessage: Message | null;
  lastActivityAtMs: number;
}

const GENERAL_THREAD_KEY = 'general:_';

function contextTypeToFilter(type: MessageContextType): FilterKey {
  if (type === 'invoice') return 'invoices';
  if (type === 'receipt') return 'receipts';
  return 'general';
}

/**
 * Outer access gate — `useAuth()` is called unconditionally so Hook order
 * stays stable; no Hook below this line runs (no Firestore listener) unless
 * `canUseMessaging(effectiveScopeUid)` grants access.
 */
const MessagesPage: React.FC = () => {
  const { effectiveScopeUid } = useAuth();
  if (!canUseMessaging(effectiveScopeUid)) {
    return <MessagesPageDisabled />;
  }
  return <MessagesPageInner />;
};

/** Minimal placeholder rendered when the user has no messaging access (route stays reachable but inert). */
const MessagesPageDisabled: React.FC = () => {
  const { t } = useTranslation('common');
  return (
    <div className="max-w-6xl mx-auto bg-surface-raised rounded-lg shadow-md p-10 text-center text-muted">
      {t('messaging.disabled', 'Nachrichten sind derzeit nicht verfügbar.')}
    </div>
  );
};

const MessagesPageInner: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const { currentUser, effectiveScopeUid, activeRole, availableTenants, activeTenantId } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rawMessages, setRawMessages] = useState<Message[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [unreadByThread, setUnreadByThread] = useState<Record<string, number>>({});
  const [summaryByThread, setSummaryByThread] = useState<Record<string, MessageThreadContextSummary>>({});
  const [filter, setFilter] = useState<FilterKey>('all');

  // Threads whose referenced document we have already attempted to resolve
  // (regardless of success) — prevents re-fetching the same doc on re-render.
  const resolvedKeysRef = useRef<Set<string>>(new Set());

  const selectedThreadKey = searchParams.get('thread') ?? GENERAL_THREAD_KEY;

  // ─── Load the tenant's recent messages (thread-list derivation) ────────────

  useEffect(() => {
    if (!currentUser || !effectiveScopeUid) {
      setListLoading(false);
      return;
    }
    setListLoading(true);
    setListError(null);

    const q = query(
      collection(db, 'messages'),
      where('userId', '==', effectiveScopeUid),
      orderBy('createdAt', 'desc'),
      fsLimit(THREAD_SCAN_LIMIT),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const msgs: Message[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const rawContext = data.context;
          if (typeof rawContext !== 'object' || rawContext === null) return;
          const contextType = rawContext.type;
          if (contextType !== 'general' && contextType !== 'invoice' && contextType !== 'receipt') return;
          const authorRole = data.authorRole;
          if (authorRole !== 'owner' && authorRole !== 'advisor') return;
          if (!data.createdAt || typeof data.createdAt.toDate !== 'function') return;
          msgs.push({
            id: docSnap.id,
            userId: data.userId,
            context: { type: contextType, refId: typeof rawContext.refId === 'string' ? rawContext.refId : null },
            refCollection:
              data.refCollection === 'received_invoices' || data.refCollection === 'scanned_receipts'
                ? data.refCollection
                : null,
            threadKey: data.threadKey,
            authorUid: data.authorUid,
            authorRole,
            text: data.text,
            createdAt: data.createdAt,
          });
        });
        setRawMessages(msgs);
        setListLoading(false);
      },
      (err) => {
        logger.error('MessagesPage: thread-list snapshot error', err);
        setListError(t('messaging.loadError'));
        setListLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, effectiveScopeUid, t]);

  // Clear the global nav badge on opening the message center (§5.3 / §9.5).
  useEffect(() => {
    if (!currentUser || !effectiveScopeUid) return;
    markGlobalRead(effectiveScopeUid, currentUser.uid).catch((err) => {
      logger.warn('MessagesPage: markGlobalRead failed', err instanceof Error ? err : new Error(String(err)));
    });
  }, [currentUser, effectiveScopeUid]);

  // ─── Derive the thread list (general always pinned, even with 0 messages) ──

  const threads = useMemo<ThreadSummary[]>(() => {
    const byKey = new Map<string, ThreadSummary>();
    byKey.set(GENERAL_THREAD_KEY, {
      threadKey: GENERAL_THREAD_KEY,
      context: { type: 'general', refId: null },
      refCollection: null,
      lastMessage: null,
      lastActivityAtMs: 0,
    });

    for (const msg of rawMessages) {
      const existing = byKey.get(msg.threadKey);
      const activityMs = msg.createdAt.toMillis();
      if (!existing || activityMs > existing.lastActivityAtMs) {
        byKey.set(msg.threadKey, {
          threadKey: msg.threadKey,
          context: msg.context,
          refCollection: msg.refCollection ?? null,
          lastMessage: msg,
          lastActivityAtMs: activityMs,
        });
      }
    }

    const list = Array.from(byKey.values());
    list.sort((a, b) => {
      if (a.threadKey === GENERAL_THREAD_KEY) return -1;
      if (b.threadKey === GENERAL_THREAD_KEY) return 1;
      return b.lastActivityAtMs - a.lastActivityAtMs;
    });
    return list;
  }, [rawMessages]);

  const filteredThreads = useMemo(
    () => (filter === 'all' ? threads : threads.filter((th) => contextTypeToFilter(th.context.type) === filter)),
    [threads, filter],
  );

  // ─── Resolve human titles for invoice/receipt threads (FIX 1 + FIX 2) ──────
  //
  // The center — unlike the row buttons — does not have the referenced row
  // loaded, so it resolves each thread's document by id via `getDoc` (no new
  // Firestore index: single-doc reads) and derives a `MessageThreadContextSummary`
  // with the SAME field/format mapping as the row buttons (see `threadSummary`).
  // Each thread is resolved at most once (dedupe via `resolvedKeysRef`).

  const summaryLabels = useMemo<ThreadSummaryLabels>(
    () => ({
      unknownBuyer: t('archive.table.values.unknown', { ns: 'invoice', defaultValue: 'Unbekannt' }),
      zugferd: t('archive.filter.zugferd', { ns: 'invoice', defaultValue: 'ZUGFeRD' }),
      xrechnung: t('archive.filter.xrechnung', { ns: 'invoice', defaultValue: 'XRechnung' }),
    }),
    [t],
  );

  useEffect(() => {
    const toLoad = threads.filter(
      (th) =>
        th.context.type !== 'general' &&
        typeof th.context.refId === 'string' &&
        th.context.refId.length > 0 &&
        !resolvedKeysRef.current.has(th.threadKey),
    );
    if (toLoad.length === 0) return;

    // Mark up-front so a re-render mid-flight cannot start a duplicate read.
    toLoad.forEach((th) => resolvedKeysRef.current.add(th.threadKey));

    let cancelled = false;

    void Promise.all(
      toLoad.map(async (th) => {
        const refId = th.context.refId as string;
        const collectionName =
          th.context.type === 'invoice'
            ? 'invoices'
            : th.refCollection === 'scanned_receipts'
              ? 'scanned_receipts'
              : 'received_invoices';
        try {
          const snap = await getDoc(doc(db, collectionName, refId));
          if (!snap.exists()) return null;
          const summary = buildThreadSummary(
            th.context.type,
            th.refCollection ?? null,
            refId,
            snap.data() as ThreadDocData,
            summaryLabels,
            i18n.language,
          );
          return summary ? ([th.threadKey, summary] as const) : null;
        } catch (err) {
          logger.warn('MessagesPage: thread summary getDoc failed', {
            threadKey: th.threadKey,
            err: err instanceof Error ? err.message : String(err),
          });
          return null;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      const valid = entries.filter(
        (e): e is readonly [string, MessageThreadContextSummary] => e !== null,
      );
      if (valid.length > 0) {
        setSummaryByThread((prev) => ({ ...prev, ...Object.fromEntries(valid) }));
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads.map((th) => th.threadKey).join(','), summaryLabels]);

  // ─── Per-thread unread badges (best-effort — see module docblock) ──────────

  useEffect(() => {
    if (!currentUser || !effectiveScopeUid || threads.length === 0) return;
    let cancelled = false;

    void Promise.all(
      threads.map(async (th) => {
        try {
          const count = await getThreadUnreadCount(th.context, effectiveScopeUid, currentUser.uid);
          return [th.threadKey, count] as const;
        } catch (err) {
          logger.warn('MessagesPage: unread count failed for thread', { threadKey: th.threadKey, err });
          return [th.threadKey, 0] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setUnreadByThread(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
    // Re-computed whenever the thread set or the selection changes (selection
    // change ⇒ a markThreadRead may have just happened in MessageThreadView).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, effectiveScopeUid, threads.map((th) => th.threadKey).join(','), selectedThreadKey]);

  const selectThread = useCallback(
    (threadKey: string) => {
      setSearchParams(threadKey === GENERAL_THREAD_KEY ? {} : { thread: threadKey });
    },
    [setSearchParams],
  );

  const counterpartyName = useMemo(() => {
    if (activeRole === 'advisor') {
      // Advisor viewing a client tenant: label the mandate by its display name.
      return availableTenants.find((tn) => tn.tenantId === activeTenantId)?.displayName ?? '';
    }
    // Owner: label by the (first) advisor mandate's display name, if any.
    return availableTenants.find((tn) => tn.role === 'advisor')?.displayName ?? t('messaging.advisor');
  }, [activeRole, availableTenants, activeTenantId, t]);

  const threadTitle = useCallback(
    (th: ThreadSummary): string => {
      if (th.context.type === 'general') return t('messaging.general');
      // FIX 1: prefer the human title from the resolved referenced document;
      // fall back to the refId-based label until it loads / if the doc is gone.
      const resolvedTitle = summaryByThread[th.threadKey]?.title;
      if (resolvedTitle) return resolvedTitle;
      if (th.context.type === 'invoice') return t('messaging.threadForInvoice', { number: th.context.refId ?? '' });
      return t('messaging.threadForReceipt', { name: th.context.refId ?? '' });
    },
    [t, summaryByThread],
  );

  const threadIcon = (type: MessageContextType): React.ReactNode => {
    if (type === 'invoice') return <IconInvoice className="shrink-0" />;
    if (type === 'receipt') return <IconExpense className="shrink-0" />;
    return <IconMessages className="shrink-0" />;
  };

  const selected = threads.find((th) => th.threadKey === selectedThreadKey) ?? threads[0] ?? null;
  const noThreadsAtAll = !listLoading && !listError && rawMessages.length === 0;

  return (
    <div className="max-w-6xl mx-auto bg-surface-raised rounded-lg shadow-md h-[calc(100vh-220px)] flex overflow-hidden">
      {/* Thread-list column — hidden on mobile once a thread is selected */}
      <aside
        className={`w-full lg:w-[340px] shrink-0 border-r border-hairline flex-col ${
          selected ? 'hidden lg:flex' : 'flex'
        }`}
      >
        <div className="p-3 flex gap-2 border-b border-hairline overflow-x-auto">
          {(
            [
              ['all', t('messaging.filterAll', 'Alle')],
              ['general', t('messaging.filterGeneral', 'Allgemein')],
              ['invoices', t('messaging.filterInvoices', 'Rechnungen')],
              ['receipts', t('messaging.filterReceipts', 'Belege')],
            ] as [FilterKey, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap ${
                filter === key ? 'bg-indigo-600 text-[#0C1322]' : 'bg-surface-inset text-secondary hover:bg-surface-hover'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <ul role="list" className="flex-1 overflow-y-auto">
          {listLoading && <li className="px-4 py-6 text-muted text-sm text-center">{t('loading')}</li>}
          {listError && !listLoading && (
            <li className="px-4 py-4 text-[var(--tone-danger-fg)] text-sm">{listError}</li>
          )}
          {!listLoading &&
            !listError &&
            filteredThreads.map((th) => {
              const unread = unreadByThread[th.threadKey] ?? 0;
              const isSelected = th.threadKey === selectedThreadKey;
              return (
                <li key={th.threadKey}>
                  <button
                    type="button"
                    onClick={() => selectThread(th.threadKey)}
                    className={`w-full text-left px-4 py-3 border-b border-hairline hover:bg-surface-inset transition-colors ${
                      isSelected ? 'bg-surface-inset border-l-2 border-indigo-500 pl-[14px]' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`flex items-center gap-1.5 truncate ${
                          unread > 0 ? 'font-semibold text-primary' : 'text-secondary'
                        }`}
                      >
                        {unread > 0 && <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" aria-hidden="true" />}
                        {threadIcon(th.context.type)}
                        <span className="truncate">{threadTitle(th)}</span>
                      </span>
                      {unread > 0 && (
                        <span
                          aria-label={t('messaging.unreadCount', { count: unread })}
                          className="bg-indigo-500 text-[#0C1322] text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shrink-0"
                        >
                          {unread > 99 ? '99+' : unread}
                        </span>
                      )}
                    </div>
                    <p className="text-muted text-sm truncate mt-0.5">
                      {th.lastMessage ? th.lastMessage.text : t('messaging.empty')}
                    </p>
                    {th.lastMessage && (
                      <p className="text-faint text-xs mt-0.5">
                        {th.lastMessage.authorRole === 'advisor' ? t('messaging.advisor') : t('messaging.owner')}
                      </p>
                    )}
                  </button>
                </li>
              );
            })}
        </ul>
      </aside>

      {/* Conversation column */}
      <div className={`flex-1 min-w-0 min-h-0 flex-col ${selected ? 'flex' : 'hidden lg:flex'}`}>
        {selected ? (
          <>
            {/* Mobile back row */}
            <div className="lg:hidden px-4 py-2 border-b border-hairline">
              <button
                type="button"
                onClick={() => selectThread(GENERAL_THREAD_KEY)}
                className="text-accent-ink text-sm"
              >
                {t('messaging.back', '‹ Zurück zur Liste')}
              </button>
            </div>
            {selected.context.type === 'general' && (
              <div className="px-4 py-2 text-muted text-sm border-b border-hairline hidden lg:block">
                {t('messaging.withCounterparty', { name: counterpartyName, defaultValue: 'mit {{name}}' })}
              </div>
            )}
            <MessageThreadView
              context={selected.context}
              refCollection={selected.refCollection}
              contextSummary={
                selected.context.type === 'general' ? undefined : summaryByThread[selected.threadKey]
              }
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-faint text-sm p-6 text-center">
            {noThreadsAtAll
              ? activeRole === 'advisor'
                ? t('messaging.noThreadsAdvisorSubtext', 'Sobald Ihr Mandant schreibt, erscheinen die Verläufe hier.')
                : t('messaging.noThreadsOwnerSubtext', 'Schreiben Sie Ihrer Kanzlei die erste Nachricht.')
              : t('messaging.noSelection', 'Wählen Sie links einen Verlauf.')}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
